import type { MarketDataCardConfig } from '@talos/kyoko';
import {
  ButtonVariants,
  CardViewEnum,
  EMPTY_OBJECT,
  FilterInput,
  FormControlSizes,
  IconName,
  MarketDataCardState,
  MixpanelEvent,
  MixpanelEventProperty,
  Portal,
  Tooltip,
  useDisclosure,
  useMixpanel,
} from '@talos/kyoko';
import type { IDockviewPanelProps } from 'dockview';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v1 as uuid } from 'uuid';
import { MarketDataCards } from '../../../../../containers/Trading/Markets/MarketDataCards';
import { fillToNumColumns } from '../../../../../providers/AppConfigProvider/fillToNumColumns';
import { useTradingSettings } from '../../../../../providers/TradingSettingsContext';
import type { MarketCardPanelConfig } from '../../../../../types/LayoutConfig';
import { Panel } from '../../../../Layout/Components/Panel';
import { TabIconButton, TabMenuItem } from '../../../../Layout/styles';
import { MarketCardsConfigProvider } from './MarketCardsConfigContext';
import { TabSettingsDialog } from './TabSettingsDialog';
import type { MarketDataCardsContextProps } from './types';

export function MarketDataCardsPanel({ api, containerApi, params }: IDockviewPanelProps<MarketCardPanelConfig>) {
  const id = api.id;
  const tabMenuPortalId = `tabMenu_${id}`;
  const rightComponentPortalId = `rightComponent_${id}`;

  const { columns: _columns, sortCards, defaults, forceCompact = false } = params;
  const [cardFilter, setCardFilter] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [isShowingPlaceholders, setIsShowingPlaceholders] = useState(false);
  const mixpanel = useMixpanel();

  const handleToggleForceCompact = useCallback(() => {
    mixpanel.track(MixpanelEvent.ToggleForceCompactMarketCards, {
      [MixpanelEventProperty.Enabled]: !forceCompact,
    });
    api.updateParameters({ forceCompact: !forceCompact });
  }, [api, forceCompact, mixpanel]);

  const marketCardsConfig = useMemo(() => ({ forceCompact }), [forceCompact]);
  const { alwaysShowMarketCardBestBidOffer, defaultNewMarketCardsCompact } = useTradingSettings();
  const columns = useMemo(() => {
    const columns = fillToNumColumns(_columns ?? []);
    if (isAdding) {
      return columns;
    } else {
      return columns.map(excludeNewCardState);
    }
  }, [_columns, isAdding]);

  const tabSettingsDialog = useDisclosure();
  // Dockview parameter updates feed back through React asynchronously, so callbacks can otherwise
  // capture stale columns while several card operations happen back-to-back. This ref always points
  // at the latest committed columns and acts like the "current state" for functional updates below.
  const columnsRef = useRef(columns);
  useEffect(() => {
    columnsRef.current = columns;
  }, [columns]);

  const handleSortCards = useCallback(() => {
    mixpanel.track(MixpanelEvent.SortMarketsTab);
    api.updateParameters({ sortCards: !sortCards });
  }, [api, sortCards, mixpanel]);

  const handleDeleteTab = useCallback(() => {
    mixpanel.track(MixpanelEvent.MarketTabClose);
    tabSettingsDialog.close();
    api.close();
  }, [api, tabSettingsDialog, mixpanel]);

  const handleAddCard = useCallback(() => {
    mixpanel.track(!isAdding ? MixpanelEvent.AddCardStart : MixpanelEvent.AddCardCancel);
    setIsShowingPlaceholders(!isAdding);
    setIsAdding(!isAdding);
    api.updateParameters({
      columns: columns.map(excludeNewCardState),
    });
  }, [isAdding, columns, mixpanel, api]);

  const updateColumns = useCallback(
    (nextColumns: MarketDataCardConfig[][] | ((prevColumns: MarketDataCardConfig[][]) => MarketDataCardConfig[][])) => {
      // Resolve from the ref, not the callback closure, so concurrent edits (save/move/delete/create)
      // compose from the newest columns even if React has not yet re-rendered with updated params.
      const resolvedColumns = typeof nextColumns === 'function' ? nextColumns(columnsRef.current) : nextColumns;
      columnsRef.current = resolvedColumns;
      api.updateParameters({ columns: resolvedColumns });
    },
    [api]
  );

  const createCard = useCallback(
    (index: number) => {
      mixpanel.track(MixpanelEvent.CreateMarketCard);
      updateColumns(prevColumns =>
        prevColumns.map((column, columnIndex) => {
          if (columnIndex === index) {
            return [
              {
                id: uuid(),
                activeSlideIndex: 1,
                availableMarketAccounts: [],
                selectedMarketAccounts: [],
                selectedMarkets: [],
                availableMarkets: [],
                view: defaultNewMarketCardsCompact ? CardViewEnum.COMPACT : CardViewEnum.NORMAL,
                state: MarketDataCardState.NEW_CARD,

                topOfBookSizeBucket: '1',
                sizeBuckets: [],
                priceIncrement: '1',
                alwaysDisplayBestBidOffer: alwaysShowMarketCardBestBidOffer,
              } satisfies Partial<MarketDataCardConfig>,
              ...column,
            ];
          }
          return column;
        })
      );
      setIsShowingPlaceholders(false);
    },
    [mixpanel, updateColumns, alwaysShowMarketCardBestBidOffer, defaultNewMarketCardsCompact]
  );

  const moveCard = useCallback<MarketDataCardsContextProps['moveCard']>(
    result => {
      const { source, destination, draggableId } = result;

      // Dropped outside the list
      if (!destination) {
        return;
      }

      const sourceColumnIndex = parseInt(source.droppableId);
      const destinationColumnIndex = parseInt(destination.droppableId);
      const card = columnsRef.current.flat().find(card => card.id === draggableId);

      if (card === undefined || (sourceColumnIndex === destinationColumnIndex && source.index === destination.index)) {
        return;
      }

      mixpanel.track(MixpanelEvent.MovedCard, { [MixpanelEventProperty.ColumnIndex]: destination.index });

      updateColumns(prevColumns =>
        prevColumns.map((column, columnIndex) => {
          let nextColumn: MarketDataCardConfig[] = [...column];
          if (columnIndex === sourceColumnIndex) {
            nextColumn = nextColumn.filter(columnCard => columnCard.id !== draggableId);
          }
          if (columnIndex === destinationColumnIndex) {
            nextColumn.splice(destination.index, 0, card);
          }
          return nextColumn;
        })
      );
    },
    [mixpanel, updateColumns]
  );

  const saveCard = useCallback(
    (nextCard: MarketDataCardConfig) => {
      mixpanel.track(MixpanelEvent.UpdateMarketCard);
      updateColumns(prevColumns =>
        prevColumns.map(column =>
          column.map(prevCard => {
            if (prevCard.id === nextCard.id) {
              return {
                ...prevCard,
                ...nextCard,
                state: MarketDataCardState.UPDATED_CARD,
              };
            }
            return prevCard;
          })
        )
      );
      setIsAdding(false);
    },
    [mixpanel, updateColumns]
  );

  const deleteCard = useCallback(
    (card: MarketDataCardConfig) => {
      mixpanel.track(MixpanelEvent.DeleteCard);
      updateColumns(prevColumns => prevColumns.map(column => column.filter(prevCard => prevCard.id !== card.id)));
    },
    [mixpanel, updateColumns]
  );

  const hidePlaceholders = useCallback(() => {
    setIsShowingPlaceholders(false);
  }, []);

  const isLastMarketTab = containerApi.panels.filter(panel => panel.api.component === 'MarketDataCards').length === 1;

  const handleConfirm = useCallback(
    ({ defaults, label }: { defaults: Record<string, any>; label: string }): void => {
      mixpanel.track(MixpanelEvent.SaveMarketTabSettings);
      tabSettingsDialog.close();
      api.setTitle(label);
      api.updateParameters({
        defaults,
      });
      if (label !== api.title) {
        mixpanel.track(MixpanelEvent.RenameTab);
        api.updateParameters({
          customTitle: true,
        });
      }
    },
    [api, mixpanel, tabSettingsDialog]
  );

  return (
    <>
      <TabSettingsDialog
        {...tabSettingsDialog}
        label={api.title ?? ''}
        defaults={params.defaults ?? EMPTY_OBJECT}
        canDeleteTab={!isLastMarketTab}
        onConfirm={handleConfirm}
        onCancel={() => {
          mixpanel.track(MixpanelEvent.CloseMarketTabSettings);
          tabSettingsDialog.close();
        }}
        onDelete={handleDeleteTab}
        showCancel={false}
        showConfirm={false}
        showClose={true}
        width={400}
      />
      <Portal portalId={rightComponentPortalId}>
        <Tooltip usePortal={false} tooltip="Settings for current market tab" placement="bottom">
          <TabIconButton
            onClick={() => {
              tabSettingsDialog.open();
            }}
            icon={IconName.Cog}
            ghost={!tabSettingsDialog.isOpen}
            variant={ButtonVariants.Priority}
            size={FormControlSizes.Small}
          />
        </Tooltip>
        <Tooltip usePortal={false} tooltip="Filter market cards by symbol" placement="bottom">
          <FilterInput
            value={cardFilter}
            onChange={setCardFilter}
            placeholder="By symbol"
            mixpanelEvent={MixpanelEvent.SearchMarketsTab}
            data-testid="filter-cards-button"
          />
        </Tooltip>
        <Tooltip
          usePortal={false}
          tooltip={forceCompact ? 'Restore cards to their configured view' : 'Show all cards in compact view'}
          placement="bottom"
        >
          <TabIconButton
            onClick={handleToggleForceCompact}
            icon={IconName.ListCollapse}
            ghost={!forceCompact}
            variant={ButtonVariants.Priority}
            size={FormControlSizes.Small}
            data-testid="toggle-compact-cards-button"
          />
        </Tooltip>
        <Tooltip usePortal={false} tooltip="Sort cards by symbol and title" placement="bottom">
          <TabIconButton
            onClick={handleSortCards}
            icon={IconName.SortDescending}
            ghost={!sortCards}
            variant={ButtonVariants.Priority}
            size={FormControlSizes.Small}
          />
        </Tooltip>
        <Tooltip usePortal={false} tooltip="Add card" placement="bottom">
          <TabIconButton
            onClick={handleAddCard}
            icon={IconName.GridAdd}
            ghost={!isAdding}
            variant={ButtonVariants.Priority}
            size={FormControlSizes.Small}
            data-testid="add-card-button"
          />
        </Tooltip>
      </Portal>
      <Portal portalId={tabMenuPortalId}>
        <TabMenuItem
          startIcon={IconName.Cog}
          ghost
          onClick={() => {
            tabSettingsDialog.open();
            mixpanel.track(MixpanelEvent.ViewMarketsTabSettings);
          }}
        >
          Settings
        </TabMenuItem>
      </Portal>
      <Panel panelApi={api} data-testid="market-cards-panel">
        <MarketCardsConfigProvider value={marketCardsConfig}>
          <MarketDataCards
            columns={columns}
            cardFilter={cardFilter}
            sortCards={sortCards}
            columnsTabID={id}
            selectedTabID={id}
            isAddingCard={isAdding}
            isShowingPlaceholders={isShowingPlaceholders}
            defaultSubAccountId={defaults?.defaultSubAccountId}
            moveCard={moveCard}
            createCard={createCard}
            deleteCard={deleteCard}
            saveCard={saveCard}
            hidePlaceholders={hidePlaceholders}
            handleAddCard={handleAddCard}
          />
        </MarketCardsConfigProvider>
      </Panel>
    </>
  );
}

const excludeNewCardState = (column: MarketDataCardConfig[]): MarketDataCardConfig[] =>
  column.filter(card => card.state !== MarketDataCardState.NEW_CARD);
